/* eslint-disable react-hooks/exhaustive-deps */
import * as yup from "yup";

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import AutoComplete from "@/components/hookForms/autoComplete";
import TextInput from "@/components/hookForms/textField";
import messages from "@/utils/messages";
import { Title } from "@/components/title/style";
import InputNumber from "@/components/hookForms/inputNumber";
import {
    classeOptions,
    estadosOptions,
    statusOptions,
    tipoCalculoDialogOptions,
    tipoRebateDialogOptions,
} from "@/utils/constants";
import IGenericResult from "@/interfaces/IGenericResult";
import serviceRebalanceUsina from "@/services/serviceRebalanceUsina";
import serviceParametrizacaoRebate from "@/services/serviceParametrizacaoRebate";
import { LoadingButton } from "@mui/lab";

export interface IDialogProps {
    open: boolean;
    onSuccess: () => void;
    onClose?: any;
    data?: any;
    concessionariaDataOptions: IGenericResult[];
}
const Modal: React.FC<IDialogProps> = ({ open, onClose, concessionariaDataOptions, data, onSuccess }) => {
    const [usinaDataOptions, setUsinaDataOptions] = useState<any[]>([]);
    const [isDisable, setDisable] = useState(false);
    const [isLoading, setLoading] = useState(false);

    const validationSchema = yup.object().shape({
        tipoRebate: yup.number().typeError(messages.optionNotExist).required(messages.requiredField),
        tipoCalculo: yup.number().typeError(messages.optionNotExist).required(messages.requiredField),
        ativo: yup.string().typeError(messages.optionNotExist).required(messages.requiredField),
        classe: yup.number().typeError(messages.optionNotExist).notRequired().nullable(),
        estado: yup.string().typeError(messages.optionNotExist).notRequired().nullable(),
        observacao: yup.string().notRequired().nullable(),
        percentualDesconto: yup.string().notRequired().nullable(),
        valorDescontoInicial: yup.string().notRequired().nullable(),
        valorDescontoFinal: yup.string().notRequired().nullable(),
        referenciaRebalance: yup.string().required(messages.requiredField),
        concessionariaId: yup.number().typeError(messages.optionNotExist).required(messages.requiredField),
        usinaId: yup.number().typeError(messages.optionNotExist).notRequired().nullable(),
    });

    const {
        control,
        handleSubmit,
        reset,
        resetField,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            classe: "",
            estado: "",
            observacao: "",
            percentualDesconto: "",
            nomeConcessionaria: "",
            usinaId: "",
        },
    });

    useEffect(() => {
        if (data && data.concessionariaId) {
            getUsinaData({ concessionariaID: data.concessionariaId });
            reset(data);
        }
    }, [data]);

    const getUsinaData = async (values: any) => {
        if (values.concessionariaID) {
            serviceRebalanceUsina
                .getUsinasByConcessionaria(values)
                .then((result) => {
                    if (result) {
                        setUsinaDataOptions(result?.data);
                    }
                })
                .catch(() => {
                    setUsinaDataOptions([]);
                });
        }
    };

    const onSubmit = (values: any) => {
        setDisable(true);
        setLoading(true);

        const newValues = {
            ...values,
            parametrizacaRebateID: data.parametrizacaoRebateID,
            tipoRebate: values.tipoRebate - 1,
            tipoCalculo: values.tipoCalculo - 1,
            ativo: values.ativo === "Ativo" ? true : false,
            valorDescontoInicial: values.valorDescontoInicial
                ? Number(values.valorDescontoInicial.replaceAll(".", "").replaceAll(",", "."))
                : 0,
            valorDescontoFinal: values.valorDescontoFinal
                ? Number(values.valorDescontoFinal.replaceAll(".", "").replaceAll(",", "."))
                : 0,
            percentualDesconto: values.percentualDesconto
                ? Number(values.percentualDesconto.replaceAll(".", "").replaceAll(",", "."))
                : 0,
        };

        serviceParametrizacaoRebate
            .update(newValues)
            .then((result) => {
                if (result) {
                    toast.success(messages.updateSucess);
                    reset();
                    onClose();
                    onSuccess();
                }
            })
            .catch((err) => {
                toast.error(messages.updateError, err);
            })
            .finally(() => {
                setLoading(false);
                setDisable(false);
            });
    };

    return (
        <Box>
            <Dialog
                maxWidth="md"
                fullWidth
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        onClose();
                    }
                }}
                open={open}
            >
                <DialogTitle>
                    <Title>Editar Parâmetro de Rebate</Title>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            reset();
                            onClose();
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="tipoRebate"
                                    label="Tipo Rebate"
                                    options={tipoRebateDialogOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="tipoCalculo"
                                    label="Tipo de Cálculo"
                                    options={tipoCalculoDialogOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="concessionariaId"
                                    label="Concessionária"
                                    options={concessionariaDataOptions}
                                    control={control}
                                    error={errors}
                                    onInputChange={(_, __, reason) => {
                                        if (reason === "clear") {
                                            data.usinaId = "";
                                            setUsinaDataOptions([]);
                                            resetField("usinaId");
                                        }
                                    }}
                                    onChange={(idConcessionaria) => {
                                        setUsinaDataOptions([]);
                                        resetField("usinaId");
                                        if (idConcessionaria) {
                                            getUsinaData({ concessionariaID: idConcessionaria });
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <AutoComplete
                                    name="usinaId"
                                    label="Usina"
                                    options={usinaDataOptions ?? []}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputNumber
                                    name="referenciaRebalance"
                                    label="Tipo Pesquisa Cliente"
                                    control={control}
                                    error={errors}
                                    decimalScale={0}
                                    maxLength={3}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="classe"
                                    label="Classe"
                                    options={classeOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="estado"
                                    label="Estado"
                                    options={estadosOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputNumber
                                    name="valorDescontoInicial"
                                    label="Desconto - De"
                                    control={control}
                                    error={errors}
                                    decimalScale={2}
                                    maxLength={9}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputNumber
                                    name="valorDescontoFinal"
                                    label="Desconto - Até"
                                    control={control}
                                    error={errors}
                                    decimalScale={2}
                                    maxLength={9}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputNumber
                                    name="percentualDesconto"
                                    label="Rebate %"
                                    control={control}
                                    error={errors}
                                    decimalScale={2}
                                    maxLength={9}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <AutoComplete
                                    name="ativo"
                                    label="Status"
                                    options={statusOptions.slice(1)}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <TextInput name="observacao" label="Observação" control={control} error={errors} />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions style={{ display: "flex", justifyContent: "center", margin: "24px" }}>
                        <Button
                            onClick={() => {
                                reset();
                                onClose();
                            }}
                            color="warning"
                            variant="contained"
                            style={{ width: "159px", height: "44px" }}
                        >
                            Cancelar
                        </Button>
                        <LoadingButton
                            type="submit"
                            color="primary"
                            variant="contained"
                            loading={isLoading}
                            disabled={isDisable}
                            style={{ width: "159px", height: "44px" }}
                        >
                            Salvar
                        </LoadingButton>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default Modal;
